import { PencilIcon, UserIcon, CalendarIcon, UsersIcon, SettingsIcon } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { Profile } from '@/types/database.types';
import { Link } from 'react-router-dom';
import ProfileImageUpload from './ProfileImageUpload';
import { useState, useEffect } from 'react';
import { getSafeImageUrl } from '@/utils/image-utils';

interface ProfileHeaderProps {
  profile: Profile | null;
  onEdit: () => void;
  onShowFollowers: () => void;
  onShowFollowing: () => void;
  onImageUpdate?: (newImageUrl: string) => void;
}

const DEFAULT_AVATAR = 'https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png?20150327203541';

export default function ProfileHeader({ profile, onEdit, onShowFollowers, onShowFollowing, onImageUpdate }: ProfileHeaderProps) {
  const [imageError, setImageError] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  
  // Initialize avatar URL when component mounts or profile changes
  useEffect(() => {
    if (profile?.avatar_url) {
      setAvatarUrl(getSafeImageUrl(profile.avatar_url));
      setImageError(false);
    } else {
      setAvatarUrl(DEFAULT_AVATAR);
    }
  }, [profile]);
  
  const handleImageError = () => {
    setImageError(true);
    setAvatarUrl(DEFAULT_AVATAR);
  };
  
  return (
    <div className="bg-gradient-to-r from-blue-200 to-blue-300 px-4 sm:px-8 py-8 sm:py-12">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-full overflow-hidden">
            {onImageUpdate ? (
              <ProfileImageUpload
                currentImageUrl={profile?.avatar_url}
                userId={profile?.id || ''}
                onImageUpdated={onImageUpdate}
              />
            ) : (
              <img
                src={avatarUrl || DEFAULT_AVATAR}
                alt={profile?.username || 'Profile'}
                className="h-full w-full object-cover"
                onError={handleImageError}
              />
            )}
          </div>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-bold text-blue-900">
                {profile?.username || 'Anonymous'}
              </h1>
              <div className="flex items-center gap-2">
                <button
                  onClick={onEdit}
                  className="p-2 rounded-full bg-white/10 text-blue-900 hover:bg-white/20 transition-colors"
                  title="Edit profile"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <Link
                  to="/settings"
                  className="p-2 rounded-full bg-white/10 text-blue-900 hover:bg-white/20 transition-colors"
                  title="Account settings"
                >
                  <SettingsIcon className="h-5 w-5" />
                </Link>
              </div>
            </div>
            <div className="mt-2 flex items-center text-blue-800">
              <CalendarIcon className="h-5 w-5 mr-2" />
              <span>
                Member since {formatDistanceToNow(new Date(profile?.created_at || Date.now()), { addSuffix: true })}
              </span>
            </div>
            <div className="mt-4 flex flex-wrap gap-4">
              <button
                onClick={onShowFollowers}
                className="flex items-center text-blue-800 hover:text-blue-900 transition-colors"
              >
                <UsersIcon className="h-5 w-5 mr-2" />
                <span className="font-semibold text-blue-900">{profile?.followers_count || 0}</span>
                <span className="ml-1">Followers</span>
              </button>
              <button
                onClick={onShowFollowing}
                className="flex items-center text-blue-800 hover:text-blue-900 transition-colors"
              >
                <UsersIcon className="h-5 w-5 mr-2" />
                <span className="font-semibold text-blue-900">{profile?.following_count || 0}</span>
                <span className="ml-1">Following</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}